// src/components/SessionGuard.jsx
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { signOut as firebaseSignOut } from 'firebase/auth';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutos de inactividad
const WARNING_BEFORE_MS  = 5  * 60 * 1000;   // avisar 5 minutos antes

export default function SessionGuard({ children }) {
  const { user, loading } = useAuth();
  const [showWarning, setShowWarning]   = useState(false);
  const [showExpired, setShowExpired]   = useState(false);
  const [countdown, setCountdown]       = useState(300); // 5 min en segundos
  const timeoutRef  = useRef(null);
  const warningRef  = useRef(null);
  const countdownRef = useRef(null);

  const logout = useCallback(async () => {
    clearAll();
    try { await firebaseSignOut(auth); } catch(e) {}
    setShowWarning(false);
    setShowExpired(true);
  }, []);

  const clearAll = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
  };

  const startTimers = useCallback(() => {
    if (!user) return;
    clearAll();
    setShowWarning(false);

    // A los 25 min → mostrar aviso
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(300);
      // Cuenta atrás cada segundo
      countdownRef.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    // A los 30 min → cerrar sesión
    timeoutRef.current = setTimeout(() => {
      logout();
    }, SESSION_TIMEOUT_MS);
  }, [user, logout]);

  // Reiniciar temporizadores en cualquier actividad del usuario
  const resetActivity = useCallback(() => {
    if (!user || showExpired) return;
    startTimers();
  }, [user, showExpired, startTimers]);

  // Montar listeners de actividad
  useEffect(() => {
    if (!user) return;
    startTimers();

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    return () => {
      clearAll();
      events.forEach(e => window.removeEventListener(e, resetActivity));
    };
  }, [user, startTimers, resetActivity]);

  // Renovar sesión cuando el usuario pulsa "Continuar"
  const handleContinue = async () => {
    try {
      await user.getIdToken(true);
      setShowWarning(false);
      startTimers();
    } catch(e) {
      logout();
    }
  };

  if (loading) return children;

  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <>
      {children}

      {/* ── Modal de aviso ── */}
      {showWarning && !showExpired && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏱️</div>
            <h2 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 20, fontWeight: 700 }}>
              ¿Sigues ahí?
            </h2>
            <p style={{ margin: '0 0 6px', color: '#475569', fontSize: 14 }}>
              Tu sesión cerrará por inactividad en
            </p>
            <div style={{ fontSize: 36, fontWeight: 800, color: '#f59e0b', margin: '8px 0 20px' }}>
              {fmt(countdown)}
            </div>
            <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 13 }}>
              Pulsa "Continuar" para seguir trabajando.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={handleContinue} style={btnPrimary}>
                ✅ Continuar sesión
              </button>
              <button onClick={logout} style={btnSecondary}>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pantalla de sesión expirada ── */}
      {showExpired && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <h2 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 20, fontWeight: 700 }}>
              Sesión expirada
            </h2>
            <p style={{ margin: '0 0 24px', color: '#475569', fontSize: 14 }}>
              Tu sesión se ha cerrado por inactividad.<br/>
              Inicia sesión de nuevo para continuar.
            </p>
            <button
              onClick={() => { setShowExpired(false); window.location.href = '/login'; }}
              style={btnPrimary}
            >
              Iniciar sesión
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Estilos ──
const overlay = {
  position: 'fixed', inset: 0, zIndex: 99999,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(3px)',
};

const modal = {
  background: '#ffffff',
  borderRadius: 16,
  padding: '36px 32px',
  maxWidth: 380,
  width: '90%',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};

const btnPrimary = {
  background: '#1e293b', color: '#fff', border: 'none',
  borderRadius: 8, padding: '11px 24px', fontWeight: 600,
  fontSize: 14, cursor: 'pointer',
};

const btnSecondary = {
  background: '#f8fafc', color: '#374151',
  border: '1px solid #cbd5e1',
  borderRadius: 8, padding: '11px 24px', fontWeight: 500,
  fontSize: 14, cursor: 'pointer',
};
